import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircleOutline as CheckIcon,
  DeleteOutline as DeleteIcon,
  Email as EmailIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  PaidOutlined as PaidIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import { SIGNUP_STATUS_LABELS as STATUS_LABELS } from "./signupStatusLabels";

// Statuses that demand a manual decision from the admin. Drives:
//   - header summary line (warning vs success tone)
//   - whether a user appears in the main list vs the collapsed "OK" group
const ATTENTION_STATUSES = new Set([
  "signed_no_pass_unpaid",
  "signed_pass_exhausted",
  "signed_on_site_unpaid",
  "failed",
  "skipped_payment_required",
  "waitlisted",
]);

// Short two-letter date used in row entries to keep cards compact (e.g.
// "12.06 14:00"). Full year is dropped — context of the dialog is always
// "just-created lessons" so the year is implied.
const formatRowDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" });
  const time = d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
};

// Used only for the email-status caption — the recipient cares about
// "when was it sent" so we show full date+time without seconds.
const formatSentAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("pl-PL")} ${d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
};

const initialsOf = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
};

const groupByUser = (perLessonReports) => {
  const map = new Map();
  perLessonReports.forEach((lessonReport) => {
    (lessonReport.results || []).forEach((row) => {
      if (!map.has(row.user_id)) {
        map.set(row.user_id, {
          user_id: row.user_id,
          user_full_name: row.user_full_name,
          user_email: row.user_email,
          rows: [],
        });
      }
      map.get(row.user_id).rows.push({
        ...row,
        lesson_id: lessonReport.lesson_id,
        lesson_start: lessonReport.lesson_start,
        lesson_type_name: lessonReport.lesson_type_name,
      });
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    (a.user_full_name || "").localeCompare(b.user_full_name || "", "pl")
  );
};

const summarize = (perUser, overrides = {}) => {
  let unpaidCount = 0;
  let unpaidAmount = 0;
  let exhaustedUsers = 0;
  let waitlisted = 0;
  let failed = 0;
  let onSiteUnpaid = 0;
  let skippedPayment = 0;
  let emailsSent = 0;
  let emailsPending = 0;
  perUser.forEach((u) => {
    let userExhausted = false;
    u.rows.forEach((r) => {
      const key = `${r.lesson_id}:${r.user_id}`;
      const override = overrides[key] || {};
      const effectiveStatus = override.status || r.status;
      const effectiveAmount = override.amount_owed !== undefined ? override.amount_owed : r.amount_owed;
      // Email-status counts only make sense for rows that triggered the
      // admin-signup reminder flow (signed_no_pass_unpaid). Other statuses
      // never enqueue an email so we don't pollute the metric with them.
      if (effectiveStatus === "signed_no_pass_unpaid") {
        unpaidCount += 1;
        unpaidAmount += effectiveAmount || 0;
        const sentAt = (override.payment_reminder_sent_at !== undefined
          ? override.payment_reminder_sent_at
          : r.payment_reminder_sent_at);
        const pending = (override.payment_reminder_pending !== undefined
          ? override.payment_reminder_pending
          : r.payment_reminder_pending);
        if (sentAt) emailsSent += 1;
        else if (pending) emailsPending += 1;
      }
      if (effectiveStatus === "signed_pass_exhausted") userExhausted = true;
      if (effectiveStatus === "waitlisted") waitlisted += 1;
      if (effectiveStatus === "failed") failed += 1;
      if (effectiveStatus === "signed_on_site_unpaid") onSiteUnpaid += 1;
      if (effectiveStatus === "skipped_payment_required") skippedPayment += 1;
    });
    if (userExhausted) exhaustedUsers += 1;
  });
  return {
    unpaidCount,
    unpaidAmount,
    exhaustedUsers,
    waitlisted,
    failed,
    onSiteUnpaid,
    skippedPayment,
    emailsSent,
    emailsPending,
  };
};

// True if any of this user's rows requires admin attention given the current
// override map (so a user disappears from the attention section as soon as
// their last problematic row is resolved).
const userNeedsAction = (user, overrides) =>
  user.rows.some((r) => {
    const key = `${r.lesson_id}:${r.user_id}`;
    const status = (overrides[key] || {}).status || r.status;
    return ATTENTION_STATUSES.has(status);
  });

const CreationReportDialog = ({
  open,
  onClose,
  perLessonReports,
  mode = "committed",   // "preview" | "committed"
  onCommit,             // called when admin confirms in preview mode
  commitBusy = false,   // spinner on confirm button
}) => {
  const perUser = useMemo(() => groupByUser(perLessonReports || []), [perLessonReports]);
  const isPreview = mode === "preview";

  const [rowOverrides, setRowOverrides] = useState({}); // key=lesson_id:user_id -> {note, status, amount_owed}
  const [rowBusy, setRowBusy] = useState({});
  const [addPassFor, setAddPassFor] = useState(null); // user object
  const [okSectionOpen, setOkSectionOpen] = useState(false);
  // Kebab menu state. We track the anchor element AND the row so the same
  // <Menu/> instance can be reused across all rows — opening on a different
  // kebab simply re-anchors. Closing on action keeps the UI snappy.
  const [menuState, setMenuState] = useState({ anchorEl: null, row: null });
  const openRowMenu = (event, row) => setMenuState({ anchorEl: event.currentTarget, row });
  const closeRowMenu = () => setMenuState({ anchorEl: null, row: null });

  const summary = useMemo(() => summarize(perUser, rowOverrides), [perUser, rowOverrides]);

  // Split users into "needs action" (top, expanded) vs "OK" (bottom,
  // collapsed). Recomputed on every override change so resolved users move
  // down automatically.
  const usersNeedingAction = useMemo(
    () => perUser.filter((u) => userNeedsAction(u, rowOverrides)),
    [perUser, rowOverrides]
  );
  const usersOk = useMemo(
    () => perUser.filter((u) => !userNeedsAction(u, rowOverrides)),
    [perUser, rowOverrides]
  );

  useEffect(() => {
    if (!open) {
      setRowOverrides({});
      setRowBusy({});
      setAddPassFor(null);
      setOkSectionOpen(false);
      setMenuState({ anchorEl: null, row: null });
    }
  }, [open]);

  const rowKey = (lessonId, userId) => `${lessonId}:${userId}`;
  const setBusy = (key, val) => setRowBusy((p) => ({ ...p, [key]: val }));
  const overrideRow = (key, patch) => setRowOverrides((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));

  const handleMarkPaid = async (row) => {
    const key = rowKey(row.lesson_id, row.user_id);
    setBusy(key, "paid");
    try {
      const headers = await getAuthHeaders();
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${row.lesson_id}/users/${row.user_id}/payment`,
        { payed: true },
        { headers }
      );
      overrideRow(key, { note: "Oznaczone jako opłacone", status: "manually_paid", amount_owed: 0 });
    } catch (e) {
      overrideRow(key, { note: `Błąd: ${e?.response?.data?.detail || e.message}` });
    } finally {
      setBusy(key, null);
    }
  };

  const handleRemove = async (row) => {
    const key = rowKey(row.lesson_id, row.user_id);
    if (!window.confirm(`Wypisać ${row.user_full_name} z zajęć ${row.lesson_type_name}?`)) return;
    setBusy(key, "remove");
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${process.env.REACT_APP_BACKEND_ADDRESS}/user_from_lesson/`, {
        headers,
        data: { lesson_id: row.lesson_id, user_id: row.user_id },
      });
      overrideRow(key, { note: "Wypisano z zajęć", status: "removed", amount_owed: 0 });
    } catch (e) {
      overrideRow(key, { note: `Błąd: ${e?.response?.data?.detail || e.message}` });
    } finally {
      setBusy(key, null);
    }
  };

  // Re-send the admin-signup payment-reminder email for a single row. The
  // backend regenerates the token and re-queues the worker; the new
  // sent_at timestamp lands asynchronously, so we optimistically tag the
  // override with a "wysyłanie..." note.
  const handleSendReminder = async (row) => {
    const key = rowKey(row.lesson_id, row.user_id);
    setBusy(key, "reminder");
    try {
      const headers = await getAuthHeaders();
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${row.lesson_id}/users/${row.user_id}/send-payment-reminder`,
        {},
        { headers }
      );
      const status = resp.data?.status;
      if (status === "queued") {
        // Backend confirmed the email is enqueued in Celery — not yet sent.
        // Mark the row as pending so the UI honestly reflects in-flight
        // state; the worker will flip payment_reminder_sent_at in the DB
        // after the actual delivery succeeds (visible on next report load).
        overrideRow(key, {
          note: "Email z linkiem do płatności zakolejkowany do wysyłki.",
          payment_reminder_sent_at: null,
          payment_reminder_pending: true,
        });
      } else if (status === "skipped_already_settled") {
        overrideRow(key, { note: "Pominięto — płatność jest już uregulowana." });
      } else if (status === "skipped_no_email") {
        overrideRow(key, { note: "Pominięto — użytkownik nie ma adresu email." });
      } else {
        overrideRow(key, { note: "Email wysłany." });
      }
    } catch (e) {
      overrideRow(key, { note: `Błąd: ${e?.response?.data?.detail || e.message}` });
    } finally {
      setBusy(key, null);
    }
  };

  // Bulk re-send for every row that's still unpaid + admin-signup. Used
  // when an admin wants to nudge the whole batch without clicking row by
  // row. We let the backend dedupe by skipping already-settled relations
  // server-side, so we don't have to filter perfectly here.
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const handleSendBulkReminders = async () => {
    const items = [];
    perUser.forEach((u) => {
      u.rows.forEach((r) => {
        const key = rowKey(r.lesson_id, r.user_id);
        const override = rowOverrides[key] || {};
        const status = override.status || r.status;
        if (status !== "signed_no_pass_unpaid") return;
        const sentAt = override.payment_reminder_sent_at !== undefined
          ? override.payment_reminder_sent_at
          : r.payment_reminder_sent_at;
        // Only push rows that haven't received an email yet — re-sending to
        // someone who already got it is a separate per-row action.
        if (sentAt) return;
        items.push({ lesson_id: r.lesson_id, user_id: r.user_id });
      });
    });
    if (items.length === 0) return;
    setBulkBusy(true);
    try {
      const headers = await getAuthHeaders();
      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment-reminder/batch`,
        { items },
        { headers }
      );
      const results = resp.data?.results || [];
      results.forEach((r) => {
        if (r.status === "queued") {
          // Same caveat as the per-row resend: "queued" only means the
          // task is on the broker. Reflect pending state until the worker
          // updates payment_reminder_sent_at on success.
          overrideRow(rowKey(r.lesson_id, r.user_id), {
            note: "Email z linkiem do płatności zakolejkowany do wysyłki.",
            payment_reminder_sent_at: null,
            payment_reminder_pending: true,
          });
        }
      });
      // Sending is the admin's final action here, so close the report
      // automatically — no need to click "Zamknij" afterwards.
      onClose();
    } catch (e) {
      // Per-item errors are surfaced in the response; a top-level catch is
      // for transport / auth failures only.
      setBulkError(`Nie udało się wysłać przypomnień: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Minimal user card. Header is a single 36px-tall row (avatar + name +
  // email + optional "Dodaj karnet" icon affordance). Each per-lesson entry
  // is a single line: date · lesson type, status chip, debt, email-status
  // indicator and the per-row kebab. The whole card stacks vertically on
  // mobile without any tables — keeps it readable down to 320px.
  const renderUserCard = (user, { needsAction }) => {
    const showAddPass = user.rows.some((r) => {
      const key = rowKey(r.lesson_id, r.user_id);
      const status = (rowOverrides[key] || {}).status || r.status;
      return status === "signed_no_pass_unpaid" || status === "signed_pass_exhausted";
    });
    const initials = initialsOf(user.user_full_name);
    return (
      <Card
        key={user.user_id}
        variant="outlined"
        sx={{
          borderColor: needsAction ? "warning.light" : "divider",
          borderLeft: needsAction ? "3px solid" : "1px solid",
          borderLeftColor: needsAction ? "warning.main" : "divider",
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          {/* User header row */}
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
                bgcolor: needsAction ? "warning.light" : "success.light",
                color: needsAction ? "warning.dark" : "success.dark",
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                {user.user_full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {user.user_email || "brak email"}
              </Typography>
            </Box>
            {showAddPass && (
              <Tooltip title="Dodaj karnet">
                <IconButton
                  size="small"
                  onClick={() => setAddPassFor(user)}
                  sx={{ p: 1 }}
                  aria-label="Dodaj karnet"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Per-lesson entries — one minimalist line per row */}
          <Stack spacing={0.25} sx={{ mt: 1, pl: { xs: 0, sm: 5 } }}>
            {user.rows
              .slice()
              .sort((a, b) => new Date(a.lesson_start) - new Date(b.lesson_start))
              .map((row) => {
                const key = rowKey(row.lesson_id, row.user_id);
                const override = rowOverrides[key] || {};
                const busy = rowBusy[key];
                const status = override.status || row.status;
                const amount = override.amount_owed != null ? override.amount_owed : row.amount_owed;
                const cfg = STATUS_LABELS[status] || { label: status, color: "default" };
                const showKebab = [
                  "signed_no_pass_unpaid",
                  "signed_pass_exhausted",
                  "signed_on_site_unpaid",
                ].includes(status);
                const sentAt = override.payment_reminder_sent_at !== undefined
                  ? override.payment_reminder_sent_at
                  : row.payment_reminder_sent_at;
                const pending = override.payment_reminder_pending !== undefined
                  ? override.payment_reminder_pending
                  : row.payment_reminder_pending;
                return (
                  <Box key={key} sx={{ py: 0.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ flexGrow: 1, minWidth: 0, fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}
                      >
                        <Box component="span" sx={{ color: "text.secondary", mr: 0.75 }}>
                          {formatRowDate(row.lesson_start)}
                        </Box>
                        {row.lesson_type_name}
                      </Typography>
                      <Chip
                        size="small"
                        label={cfg.label}
                        color={cfg.color}
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                      {amount > 0 && (
                        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                          {amount} zł
                        </Typography>
                      )}
                      {showKebab && (
                        <IconButton
                          size="small"
                          disabled={!!busy}
                          onClick={(e) => openRowMenu(e, row)}
                          aria-label="Akcje"
                          sx={{ p: 0.75 }}
                        >
                          {busy ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Stack>

                    {/* Pass used — name, id and expiry so the admin can see
                        exactly which pass covered this date (auto-pick may use
                        a different pass on each recurring date). */}
                    {(status === "signed_with_pass" || status === "signed_pass_exhausted") && row.pass_type_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25 }}
                      >
                        Karnet: {row.pass_type_name}
                        {row.pass_used_id ? ` (#${row.pass_used_id})` : ""}
                        {row.pass_valid_until
                          ? ` • ważny do ${new Date(row.pass_valid_until).toLocaleDateString("pl-PL")}`
                          : ""}
                      </Typography>
                    )}

                    {/* Email status — only shown for admin-signup unpaid rows.
                        Inline caption under the row, doesn't fight for space
                        with the chip/kebab line. */}
                    {status === "signed_no_pass_unpaid" && (sentAt || pending) && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.25,
                          color: sentAt ? "success.main" : "info.main",
                        }}
                      >
                        {sentAt ? (
                          <>
                            <EmailIcon fontSize="inherit" />
                            wysłano: {formatSentAt(sentAt)}
                          </>
                        ) : (
                          <>⏳ wysyłka w toku</>
                        )}
                      </Typography>
                    )}
                    {override.note && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {override.note}
                      </Typography>
                    )}
                    {row.error && (
                      <Typography variant="caption" display="block" color="error">
                        {row.error}
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ---- Header summary line ---------------------------------------------------
  // One sentence that captures everything actionable. The dialog title stays
  // small; this line carries the severity tone (warning vs success).
  const buildSummaryLine = () => {
    const parts = [];
    if (summary.unpaidCount > 0) {
      parts.push(`${summary.unpaidCount} bez karnetu (${summary.unpaidAmount} zł)`);
    }
    if (summary.exhaustedUsers > 0) parts.push(`${summary.exhaustedUsers} z wyczerpanym karnetem`);
    if (summary.waitlisted > 0) parts.push(`${summary.waitlisted} na liście oczekujących`);
    if (summary.failed > 0) parts.push(`${summary.failed} błędów`);
    if (summary.skippedPayment > 0) parts.push(`${summary.skippedPayment} pominiętych (płatność online)`);
    if (summary.onSiteUnpaid > 0) parts.push(`${summary.onSiteUnpaid} płaci na miejscu`);
    if (parts.length === 0) {
      return {
        text: `Wszyscy zapisani z karnetem (${perUser.length})`,
        tone: "success",
      };
    }
    return {
      text: parts.join(" · "),
      tone: summary.failed > 0 || summary.unpaidCount > 0 ? "warning" : "info",
    };
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const summaryLine = buildSummaryLine();
  const remainingToSend =
    summary.unpaidCount - summary.emailsSent - summary.emailsPending;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {isPreview ? "Podgląd zapisu" : "Raport zapisu"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              fontWeight: 500,
              color:
                summaryLine.tone === "warning"
                  ? "warning.main"
                  : summaryLine.tone === "success"
                  ? "success.main"
                  : "text.secondary",
            }}
          >
            {summaryLine.tone === "success" ? "✓ " : summaryLine.tone === "warning" ? "⚠ " : ""}
            {summaryLine.text}
          </Typography>
          {isPreview && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              To jest podgląd — nic nie zostało jeszcze zapisane. Potwierdź, aby utworzyć zajęcia i wysłać e-maile.
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
          {perUser.length === 0 ? (
            <Typography color="text.secondary">
              {isPreview
                ? `Zostaną utworzone zajęcia bez uczestników (${(perLessonReports || []).length}). Uczestników można dopisać później.`
                : "Brak użytkowników do raportowania."}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {usersNeedingAction.map((u) => renderUserCard(u, { needsAction: true }))}

              {usersOk.length > 0 && (
                <Box>
                  <Button
                    onClick={() => setOkSectionOpen((v) => !v)}
                    startIcon={okSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      color: "success.main",
                      fontWeight: 500,
                      pl: 0.5,
                    }}
                  >
                    <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Zapisani z karnetem ({usersOk.length})
                  </Button>
                  <Collapse in={okSectionOpen} unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {usersOk.map((u) => renderUserCard(u, { needsAction: false }))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            py: 1.5,
          }}
        >
          {isPreview ? (
            <>
              <Button
                onClick={onClose}
                variant="text"
                disabled={commitBusy}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Wróć
              </Button>
              <Button
                onClick={onCommit}
                variant="contained"
                color="primary"
                disabled={commitBusy || (perLessonReports || []).length === 0}
                startIcon={commitBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{
                  textTransform: "none",
                  ml: { sm: "auto" },
                  order: { xs: -1, sm: 0 },
                }}
              >
                {commitBusy ? "Tworzenie..." : `Potwierdź i utwórz (${perUser.length} os.)`}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={onClose}
                variant="text"
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Zamknij
              </Button>
              {remainingToSend > 0 && (
                <Button
                  onClick={handleSendBulkReminders}
                  startIcon={bulkBusy ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  disabled={bulkBusy}
                  variant="contained"
                  color="primary"
                  sx={{
                    textTransform: "none",
                    ml: { sm: "auto" },
                    order: { xs: -1, sm: 0 },
                  }}
                >
                  Wyślij e-mail z linkiem ({remainingToSend})
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(bulkError)}
        autoHideDuration={6000}
        onClose={() => setBulkError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setBulkError(null)}
          sx={{ width: "100%" }}
        >
          {bulkError}
        </Alert>
      </Snackbar>

      {/* Single shared kebab menu — re-anchored each time a row's IconButton
          is clicked. Decides which entries to show based on the currently
          targeted row's effective status. Closing the menu does NOT close
          the parent dialog (Menu uses its own portal). */}
      {(() => {
        const row = menuState.row;
        if (!row) {
          return (
            <Menu
              anchorEl={menuState.anchorEl}
              open={Boolean(menuState.anchorEl)}
              onClose={closeRowMenu}
            />
          );
        }
        const key = rowKey(row.lesson_id, row.user_id);
        const override = rowOverrides[key] || {};
        const effectiveStatus = override.status || row.status;
        const sentAt = override.payment_reminder_sent_at !== undefined
          ? override.payment_reminder_sent_at
          : row.payment_reminder_sent_at;
        const reminderEverSent = !!sentAt;
        const isUnpaid = effectiveStatus === "signed_no_pass_unpaid";
        return (
          <Menu
            anchorEl={menuState.anchorEl}
            open={Boolean(menuState.anchorEl)}
            onClose={closeRowMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {!isPreview && isUnpaid && (
              <MenuItem
                onClick={() => {
                  closeRowMenu();
                  handleMarkPaid(row);
                }}
              >
                <ListItemIcon>
                  <PaidIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText>Oznacz opłacone</ListItemText>
              </MenuItem>
            )}
            {!isPreview && isUnpaid && (
              <MenuItem
                onClick={() => {
                  closeRowMenu();
                  handleSendReminder(row);
                }}
              >
                <ListItemIcon>
                  <SendIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText>
                  {reminderEverSent ? "Wyślij e-mail ponownie" : "Wyślij e-mail z linkiem"}
                </ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                closeRowMenu();
                if (isPreview) {
                  // In preview mode: just hide this row from the plan (local-only override)
                  const key = rowKey(row.lesson_id, row.user_id);
                  overrideRow(key, { status: "removed", note: "Usunięto z podglądu" });
                } else {
                  handleRemove(row);
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>{isPreview ? "Usuń z listy" : "Wypisz z zajęć"}</ListItemText>
            </MenuItem>
          </Menu>
        );
      })()}

      <AddPassQuickDialog
        user={addPassFor}
        open={!!addPassFor}
        onClose={() => setAddPassFor(null)}
        onAdded={(passInfo) => {
          setAddPassFor(null);
          // Mark every unpaid row of this user with a hint
          perUser
            .find((u) => u.user_id === passInfo.user_id)
            ?.rows.forEach((row) => {
              if (row.status === "signed_no_pass_unpaid" || row.status === "signed_pass_exhausted") {
                overrideRow(rowKey(row.lesson_id, row.user_id), {
                  note: `Dodano karnet „${passInfo.pass_type_name}" — pamiętaj, by przypisać go do zajęć ręcznie.`,
                });
              }
            });
        }}
      />
    </>
  );
};

const AddPassQuickDialog = ({ open, user, onClose, onAdded }) => {
  const [passTypes, setPassTypes] = useState([]);
  const [selectedPassTypeId, setSelectedPassTypeId] = useState("");
  const [payed, setPayed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedPassTypeId("");
      setPayed(false);
      setError("");
      return;
    }
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, { headers });
        setPassTypes(res.data?.objects || res.data || []);
      } catch (e) {
        setError("Nie udało się pobrać typów karnetów.");
      }
    })();
  }, [open]);

  const handleSubmit = async () => {
    if (!user || !selectedPassTypeId) return;
    const passType = passTypes.find((pt) => pt.id === selectedPassTypeId);
    if (!passType) return;
    setSubmitting(true);
    setError("");
    try {
      const lessonsCount = (passType.lesson_categories_for_pass_type || []).map((cat) => ({
        lesson_category_id: cat.lesson_category_id,
        lessons_to_use: cat.is_unlimited ? 0 : parseInt(cat.lessons_to_use, 10),
        is_unlimited: cat.is_unlimited || false,
      }));
      const headers = await getAuthHeaders();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass_to_user/`,
        {
          pass_type_name: passType.name,
          user_id: user.user_id,
          lesson_categories: lessonsCount,
          valid_from: new Date().toISOString(),
          valid_until: null, // backend auto-calculates from pass type defaults
          payed,
          created_by_admin: true,
        },
        { headers }
      );
      onAdded({ user_id: user.user_id, pass_type_name: passType.name });
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się dodać karnetu.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dodaj karnet — {user?.user_full_name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="quick-pass-type-label">Typ karnetu</InputLabel>
          <Select
            labelId="quick-pass-type-label"
            label="Typ karnetu"
            value={selectedPassTypeId}
            onChange={(e) => setSelectedPassTypeId(e.target.value)}
          >
            {passTypes.map((pt) => (
              <MenuItem key={pt.id} value={pt.id}>
                {pt.name} {pt.price ? `— ${pt.price} zł` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <input
              id="quick-pass-payed"
              type="checkbox"
              checked={payed}
              onChange={(e) => setPayed(e.target.checked)}
            />
            <label htmlFor="quick-pass-payed">Karnet jest opłacony</label>
          </Stack>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedPassTypeId || submitting}
        >
          {submitting ? <CircularProgress size={18} /> : "Dodaj karnet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreationReportDialog;
